import React, { useState, useCallback } from 'react';
import { RSVP, CustomField } from '@/types';

interface RSVPListProps {
  rsvps: RSVP[];
  customFields?: CustomField[];
  eventName?: string;
  loading?: boolean;
}

function exportToCSV(rsvps: RSVP[], customFields: CustomField[], eventName: string) {
  const headers = ['Name', 'Email', 'Status', 'Guests', 'Date', ...customFields.map(f => f.label), 'Comments'];
  const rows = rsvps.map(rsvp => [
    rsvp.full_name,
    rsvp.email,
    rsvp.attending,
    String(rsvp.guest_count),
    new Date(rsvp.created_at).toLocaleDateString('en-AU'),
    ...customFields.map(f => (rsvp.custom_responses || {})[f.id] || ''),
    rsvp.comments || '',
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${sanitizeFilename(eventName)}-rsvps.csv`);
}

async function exportToXLSX(rsvps: RSVP[], customFields: CustomField[], eventName: string) {
  const XLSX = await import('xlsx');
  const headers = ['Name', 'Email', 'Status', 'Guests', 'Date', ...customFields.map(f => f.label), 'Comments'];
  const rows = rsvps.map(rsvp => [
    rsvp.full_name,
    rsvp.email,
    rsvp.attending,
    rsvp.guest_count,
    new Date(rsvp.created_at).toLocaleDateString('en-AU'),
    ...customFields.map(f => (rsvp.custom_responses || {})[f.id] || ''),
    rsvp.comments || '',
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  // Set column widths
  ws['!cols'] = headers.map((h) => ({ wch: Math.max(h.length, 12) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'RSVPs');
  XLSX.writeFile(wb, `${sanitizeFilename(eventName)}-rsvps.xlsx`);
}

async function exportToPDF(rsvps: RSVP[], customFields: CustomField[], eventName: string) {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: customFields.length > 2 ? 'landscape' : 'portrait' });

  // Title
  doc.setFontSize(16);
  doc.text(`${eventName} - RSVPs`, 14, 20);

  // Summary
  const attending = rsvps.filter(r => r.attending === 'yes').length;
  const declined = rsvps.filter(r => r.attending === 'no').length;
  const maybe = rsvps.filter(r => r.attending === 'maybe').length;
  const totalGuests = rsvps.filter(r => r.attending === 'yes').reduce((sum, r) => sum + r.guest_count, 0);

  doc.setFontSize(10);
  doc.text(`Attending: ${attending} | Declined: ${declined} | Maybe: ${maybe} | Total Guests: ${totalGuests}`, 14, 28);

  // Table
  const headers = ['Name', 'Email', 'Status', 'Guests', ...customFields.map(f => f.label), 'Comments'];
  const rows = rsvps.map(rsvp => [
    rsvp.full_name,
    rsvp.email,
    rsvp.attending,
    String(rsvp.guest_count),
    ...customFields.map(f => (rsvp.custom_responses || {})[f.id] || ''),
    rsvp.comments || '',
  ]);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 34,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [109, 40, 217] },
  });

  doc.save(`${sanitizeFilename(eventName)}-rsvps.pdf`);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function sanitizeFilename(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function RSVPDetail({ rsvp, customFields }: { rsvp: RSVP; customFields: CustomField[] }) {
  const responses = rsvp.custom_responses || {};
  const entries = customFields
    .map((field) => ({ label: field.label, value: responses[field.id] }))
    .filter((entry) => entry.value);

  if (entries.length === 0 && !rsvp.comments) {
    return (
      <div className="px-4 py-3 text-sm text-gray-400 italic">
        No additional information provided
      </div>
    );
  }

  return (
    <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {entries.map((entry) => (
        <div key={entry.label} className="min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{entry.label}</p>
          <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap break-words">{entry.value}</p>
        </div>
      ))}
      {rsvp.comments && (
        <div className="min-w-0 sm:col-span-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Comments</p>
          <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap break-words">{rsvp.comments}</p>
        </div>
      )}
    </div>
  );
}

export function RSVPList({ rsvps, customFields = [], eventName = 'Event', loading = false }: RSVPListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const handleExport = useCallback(async (format: 'csv' | 'xlsx' | 'pdf') => {
    setExporting(format);
    try {
      if (format === 'csv') exportToCSV(rsvps, customFields, eventName);
      else if (format === 'xlsx') await exportToXLSX(rsvps, customFields, eventName);
      else if (format === 'pdf') await exportToPDF(rsvps, customFields, eventName);
    } catch (err) {
      console.error(`Export to ${format} failed:`, err);
    } finally {
      setTimeout(() => setExporting(null), 500);
    }
  }, [rsvps, customFields, eventName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
      </div>
    );
  }

  if (rsvps.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <p className="text-gray-500">No RSVPs yet</p>
      </div>
    );
  }

  const attending = rsvps.filter((r) => r.attending === 'yes');
  const declined = rsvps.filter((r) => r.attending === 'no');
  const maybe = rsvps.filter((r) => r.attending === 'maybe');
  const totalGuests = attending.reduce((sum, r) => sum + r.guest_count, 0);

  const hasCustomFields = customFields.length > 0;

  const toggleExpand = (rsvpId: string) => {
    setExpandedId((prev) => (prev === rsvpId ? null : rsvpId));
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{attending.length}</p>
          <p className="text-xs text-green-600 font-medium">Attending</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{declined.length}</p>
          <p className="text-xs text-red-600 font-medium">Declined</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-amber-700">{maybe.length}</p>
          <p className="text-xs text-amber-600 font-medium">Maybe</p>
        </div>
        <div className="bg-violet-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-violet-700">{totalGuests}</p>
          <p className="text-xs text-violet-600 font-medium">Total Guests</p>
        </div>
      </div>

      {/* Export Buttons */}
      {rsvps.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500 mr-1">Export:</span>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            {exporting === 'csv' ? 'Exporting...' : 'CSV'}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            {exporting === 'xlsx' ? 'Exporting...' : 'Excel'}
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            {exporting === 'pdf' ? 'Exporting...' : 'PDF'}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-500">Name</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">Email</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">Guests</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
              {hasCustomFields && (
                <th className="text-left py-3 px-4 font-medium text-gray-500">Details</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rsvps.map((rsvp) => {
              const isExpanded = expandedId === rsvp.rsvp_id;
              const hasResponses = hasCustomFields && Object.keys(rsvp.custom_responses || {}).length > 0;

              return (
                <React.Fragment key={rsvp.rsvp_id}>
                  <tr className={`border-b border-gray-50 hover:bg-gray-50 ${isExpanded ? 'bg-gray-50' : ''}`}>
                    <td className="py-3 px-4 font-medium text-gray-900">{rsvp.full_name}</td>
                    <td className="py-3 px-4 text-gray-600">{rsvp.email}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          rsvp.attending === 'yes'
                            ? 'bg-green-100 text-green-700'
                            : rsvp.attending === 'no'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {rsvp.attending}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{rsvp.guest_count}</td>
                    <td className="py-3 px-4 text-gray-500">
                      {new Date(rsvp.created_at).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </td>
                    {hasCustomFields && (
                      <td className="py-3 px-4">
                        {hasResponses ? (
                          <button
                            onClick={() => toggleExpand(rsvp.rsvp_id)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700 transition-colors"
                          >
                            {isExpanded ? 'Hide' : 'More info'}
                            <svg
                              className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && hasCustomFields && (
                    <tr className="bg-gray-50">
                      <td colSpan={hasCustomFields ? 6 : 5} className="p-0">
                        <div className="border-l-4 border-violet-200 ml-4">
                          <RSVPDetail rsvp={rsvp} customFields={customFields} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
